import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Bell, BellOff, Plus, Trash2 } from "lucide-react";
import { subscribeToPush, unsubscribeFromPush, getPushStatus } from "../lib/push";

const DAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

export default function Reminders() {
  const [items, setItems] = useState([]);
  const [draft, setDraft] = useState({ title: "Daily check-in", time_of_day: "20:00", days: [0,1,2,3,4,5,6], enabled: true });
  const [pushStatus, setPushStatus] = useState("default");

  const load = () => api.get("/reminders").then((r) => setItems(r.data));
  useEffect(() => {
    load();
    getPushStatus().then(setPushStatus);
  }, []);

  const enablePush = async () => {
    try {
      await subscribeToPush();
      setPushStatus("subscribed");
      toast.success("Background reminders enabled");
    } catch (e) {
      toast.error(e.message || "Could not enable push");
    }
  };

  const disablePush = async () => {
    try {
      await unsubscribeFromPush();
      setPushStatus("granted");
      toast.success("Background reminders disabled");
    } catch {
      toast.error("Could not disable");
    }
  };

  const sendTestPush = async () => {
    try {
      const { data } = await api.post("/push/test");
      toast.success(`Test sent to ${data.sent} device(s)`);
    } catch {
      toast.error("Test failed");
    }
  };

  const toggleDay = (d) => {
    setDraft((s) => ({ ...s, days: s.days.includes(d) ? s.days.filter((x) => x !== d) : [...s.days, d].sort() }));
  };

  const create = async () => {
    if (!draft.title.trim() || !/^\d{2}:\d{2}$/.test(draft.time_of_day)) return;
    try { await api.post("/reminders", draft); toast.success("Reminder added"); await load(); } catch { toast.error("Could not add reminder"); }
  };
  const remove = async (id) => { await api.delete(`/reminders/${id}`); await load(); };
  const toggle = async (it) => { await api.patch(`/reminders/${it.id}`, { ...it, enabled: !it.enabled }); await load(); };

  return (
    <div className="space-y-6 fade-up" data-testid="reminders-page">
      <header>
        <h1 className="font-display text-3xl font-bold text-slate-900">Reminders</h1>
        <p className="text-slate-600 mt-2 max-w-xl">Set gentle nudges for check-ins, medication and self-care. With Web Push enabled, reminders fire even when SafeHaven is closed.</p>
      </header>

      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 flex flex-wrap items-center justify-between gap-3" data-testid="notif-status">
        <div className="flex items-center gap-2 text-sm">
          {pushStatus === "subscribed" ? <Bell className="w-4 h-4 text-teal-700" /> : <BellOff className="w-4 h-4 text-slate-400" />}
          <span className="text-slate-700">
            Background push: <strong className="capitalize">{pushStatus === "subscribed" ? "Enabled" : pushStatus === "granted" ? "Permission granted (not subscribed)" : pushStatus}</strong>
          </span>
        </div>
        <div className="flex flex-wrap gap-2">
          {pushStatus !== "subscribed" && pushStatus !== "unsupported" && (
            <button data-testid="notif-enable" onClick={enablePush} className="bg-teal-700 hover:bg-teal-800 text-white rounded-xl px-4 py-2 text-sm font-semibold">
              Enable background push
            </button>
          )}
          {pushStatus === "subscribed" && (
            <>
              <button data-testid="notif-test" onClick={sendTestPush} className="border border-slate-300 hover:bg-slate-50 rounded-xl px-4 py-2 text-sm font-semibold text-slate-700">
                Send test
              </button>
              <button data-testid="notif-disable" onClick={disablePush} className="border border-red-200 text-red-700 hover:bg-red-50 rounded-xl px-4 py-2 text-sm font-semibold">
                Disable
              </button>
            </>
          )}
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6" data-testid="reminder-form">
        <h2 className="font-display text-lg font-bold text-slate-900 mb-3">Add a reminder</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <input value={draft.title} onChange={(e) => setDraft({ ...draft, title: e.target.value })} placeholder="What's this for?" className="border border-slate-300 rounded-xl px-3 py-2.5 outline-none focus:ring-2 focus:ring-teal-500 text-sm" data-testid="reminder-title" />
          <input type="time" value={draft.time_of_day} onChange={(e) => setDraft({ ...draft, time_of_day: e.target.value })} className="border border-slate-300 rounded-xl px-3 py-2.5 outline-none focus:ring-2 focus:ring-teal-500 text-sm" data-testid="reminder-time" />
          <button data-testid="reminder-add" onClick={create} className="bg-teal-700 hover:bg-teal-800 text-white rounded-xl px-4 py-2.5 font-semibold flex items-center justify-center gap-1">
            <Plus className="w-4 h-4" /> Add reminder
          </button>
        </div>
        <div className="mt-3 flex flex-wrap gap-2">
          {DAYS.map((label, idx) => (
            <button
              key={label}
              data-testid={`reminder-day-${idx}`}
              type="button"
              onClick={() => toggleDay(idx)}
              className={`px-3 py-1.5 rounded-full text-xs font-medium border ${
                draft.days.includes(idx) ? "bg-teal-700 text-white border-teal-700" : "bg-white text-slate-600 border-slate-300"
              }`}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6" data-testid="reminder-list">
        <h2 className="font-display text-lg font-bold text-slate-900 mb-3">My reminders</h2>
        {items.length === 0 && <div className="text-sm text-slate-500" data-testid="reminders-empty">No reminders yet.</div>}
        <div className="space-y-2">
          {items.map((it) => (
            <div key={it.id} className="flex items-center justify-between border border-slate-200 rounded-xl px-4 py-3" data-testid={`reminder-item-${it.id}`}>
              <div>
                <div className="font-medium text-slate-900">{it.title}</div>
                <div className="text-xs text-slate-500">{it.time_of_day} UTC · {it.days.map((d) => DAYS[d]).join(", ")}</div>
              </div>
              <div className="flex items-center gap-2">
                <button data-testid={`reminder-toggle-${it.id}`} onClick={() => toggle(it)} className={`text-xs px-3 py-1.5 rounded-full ${it.enabled ? "bg-teal-50 text-teal-800" : "bg-slate-100 text-slate-600"}`}>
                  {it.enabled ? "On" : "Off"}
                </button>
                <button data-testid={`reminder-delete-${it.id}`} onClick={() => remove(it.id)} className="text-red-600 hover:bg-red-50 rounded-lg p-1.5">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4 text-sm text-amber-900">
        Reminder times are in UTC. With background push enabled, you'll get notifications even when SafeHaven isn't open.
      </div>
    </div>
  );
}
